import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { monospaceFontFamily } from "src/providers/theme";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { updateUniversalLicense } from "@dashboard/devices/store";
import { License } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation } from "@dashboard/groups/utils";
import { Provisioning } from "@dashboard/provisioning/types";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

type ActivationCodeCellProps = {
    license: License;
};
const useStyles = makeStyles()((theme) => ({
    activationCodeCell: {
        paddingRight: theme.spacing(8),
    },
    // Codes are GUIDs of the same length; a fixed-width font lines them, and their copy buttons, up row by row.
    activationCode: {
        fontFamily: monospaceFontFamily,
    },
    targetGroupSelect: {
        maxWidth: 400,
    },
}));

export const ActivationCodeCell = (props: ActivationCodeCellProps) => {
    const { classes } = useStyles();

    return (
        <MinimalTableCell align="left" className={classes.activationCodeCell}>
            <PaneElement.CopyableValue value={props.license.activationCode} valueClassName={classes.activationCode} />
        </MinimalTableCell>
    );
};

// The shared Tooltip wraps its child in a box of its own, which has to be full width for the select to fill the cell.
const fullWidthTooltipBox: React.CSSProperties = { display: "block", width: "100%" };

type TargetGroupCellProps = {
    license: License;
};
export const TargetGroupCell = (props: TargetGroupCellProps) => {
    const { t } = useTranslation(["pages"]);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const groups = useSelector(selectGroups);
    const isSubscriptionExpired = useCurrentSubscriptionExpired();

    const groupOptions = getNestedGroupsWithIndentation(groups);

    const handleGroupChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }

        const groupId = selectedOption.value;
        if (groupId === props.license.universalGroupId) {
            return;
        }

        try {
            await dispatch(updateUniversalLicense({ id: props.license.id, groupId })).unwrap();
            showSuccessToast(t("pages:universalLicensePage.toast.targetGroupChangedSuccessfully"));
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message || t("pages:universalLicensePage.toast.targetGroupChangeFailed"));
        }
    };

    return (
        <MinimalTableCell align="left" fluid fluidMinWidth="24ch">
            <Tooltip
                title={t("pages:universalLicensePage.tooltip.subscriptionExpired")}
                hide={!isSubscriptionExpired}
                childrenBoxStyles={fullWidthTooltipBox}
            >
                <SelectField
                    value={groupOptions.find((option) => option.value === props.license.universalGroupId)}
                    onChange={handleGroupChange}
                    disabled={isSubscriptionExpired}
                    disableSorting
                    options={groupOptions}
                    fullWidth
                    className={classes.targetGroupSelect}
                />
            </Tooltip>
        </MinimalTableCell>
    );
};

type ProvisioningCellProps = {
    provisionings: Provisioning[];
};
export const ProvisioningCell = (props: ProvisioningCellProps) => {
    const { t } = useTranslation(["pages"]);
    const navigate = useNavigate();

    return (
        <MinimalTableCell align="left">
            {props.provisionings.length
                ? props.provisionings.map((provisioning, index) => (
                      <React.Fragment key={provisioning.id}>
                          {index > 0 ? ", " : null}
                          <TextLink
                              component="button"
                              type="button"
                              tooltip={t("pages:universalLicensePage.tooltip.openThisProvisioning")}
                              onClick={() => navigate(`/dashboard/provisioning/${provisioning.id}`)}
                          >
                              {provisioning.name}
                          </TextLink>
                      </React.Fragment>
                  ))
                : t("pages:universalLicensePage.table.notUsed")}
        </MinimalTableCell>
    );
};

type ActionCellProps = {
    license: License;
    onDelete: (license: License) => void;
};
export const ActionCell = (props: ActionCellProps) => {
    const { t } = useTranslation(["modals"]);

    return (
        <MinimalTableCell align="right">
            <DangerOutlinedButton
                onClick={() => props.onDelete(props.license)}
                tooltipProps={{ title: t("modals:deleteUniversalLicense.title") }}
            >
                <i className="fa-solid fa-trash" />
            </DangerOutlinedButton>
        </MinimalTableCell>
    );
};
